"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "tableRowsPerPage";
const DEFAULT_LIMIT = 10;
const VALID_LIMITS = [10, 20, 50, 100];

interface PaginationContextType {
  limit: number;
  setLimit: (newLimit: number) => void;
  isHydrated: boolean;
}

const PaginationContext = createContext<PaginationContextType | undefined>(undefined);

export const PaginationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [limit, setLimitState] = useState(DEFAULT_LIMIT);
  const [isHydrated, setIsHydrated] = useState(false);

  // Load saved limit from localStorage on mount
  useEffect(() => {
    try {
      const savedLimit = localStorage.getItem(STORAGE_KEY);
      if (savedLimit) {
        const parsedLimit = parseInt(savedLimit, 10);
        if (VALID_LIMITS.includes(parsedLimit)) {
          setLimitState(parsedLimit);
        }
      }
    } catch (error) {
      console.warn("Error loading table pagination from localStorage:", error);
    } finally {
      setIsHydrated(true);
    }
  }, []);

  // Save limit to localStorage when it changes
  const setLimit = useCallback((newLimit: number) => {
    if (VALID_LIMITS.includes(newLimit)) {
      setLimitState(newLimit);
      try {
        localStorage.setItem(STORAGE_KEY, newLimit.toString());
      } catch (error) {
        console.warn("Error saving table pagination to localStorage:", error);
      }
    }
  }, []);

  return (
    <PaginationContext.Provider
      value={{
        limit,
        setLimit,
        isHydrated,
      }}
    >
      {children}
    </PaginationContext.Provider>
  );
};

export const usePagination = () => {
  const context = useContext(PaginationContext);
  if (context === undefined) {
    throw new Error("usePagination must be used within a PaginationProvider");
  }
  return context;
};
